import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Language = "ru" | "en";

interface LocaleState {
	/** Active language — resolved from: workspace setting > browser detect */
	language: Language;
	timezone: string;
	/** Whether the user has explicitly overridden the workspace locale */
	isOverridden: boolean;
	setLanguage: (lang: Language) => void;
	setTimezone: (tz: string) => void;
	/** Sync locale from workspace settings (called on workspace load/switch) */
	syncFromWorkspace: (wsLocale?: string, wsTz?: string) => void;
}

function detectLanguage(): Language {
	const nav = navigator.language.slice(0, 2);
	if (nav === "en") return "en";
	return "ru";
}

function asLanguage(val: string | undefined): Language | undefined {
	if (val === "ru" || val === "en") return val;
	return undefined;
}

export const useLocaleStore = create<LocaleState>()(
	persist(
		(set, get) => ({
			language: detectLanguage(),
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			isOverridden: false,

			setLanguage: (language) => set({ language, isOverridden: true }),

			setTimezone: (timezone) => set({ timezone }),

			syncFromWorkspace: (wsLocale, wsTz) => {
				const state = get();
				// Only apply workspace locale if the user hasn't explicitly overridden
				if (!state.isOverridden) {
					const lang = asLanguage(wsLocale);
					if (lang && lang !== state.language) {
						set({ language: lang });
					}
				}
				// Always sync timezone from workspace if set and not already overridden
				if (wsTz && wsTz !== state.timezone) {
					set({ timezone: wsTz });
				}
			},
		}),
		{ name: "omnios-locale" },
	),
);
